import { DICT_COMMON } from "./dict-common";
import { DICT_COCKTAILS } from "./dict-cocktails";
import { DICT_PAGES } from "./dict-pages";
import { DICT_EVENTS } from "./dict-events";
import { DICT_META } from "./dict-meta";
import { DICT_SHOP } from "./dict-shop";
import { PATTERNS } from "./patterns";

export const LOCALES = ["fr", "en", "de", "it", "es"] as const;
export type Locale = (typeof LOCALES)[number];

export const LANG_STORAGE_KEY = "cp_lang";

/** Chaque entrée : texte français → [en, de, it, es] */
export type Dict = Record<string, readonly [string, string, string, string]>;

const INDEX: Record<Exclude<Locale, "fr">, number> = { en: 0, de: 1, it: 2, es: 3 };

export const normalize = (s: string) => s.replace(/[\s  ]+/g, " ").trim();

let table: Map<string, readonly string[]> | null = null;

function getTable() {
  if (table) return table;
  table = new Map();
  for (const dict of [DICT_COMMON, DICT_COCKTAILS, DICT_PAGES, DICT_EVENTS, DICT_META, DICT_SHOP]) {
    for (const [fr, values] of Object.entries(dict)) table.set(normalize(fr), values);
  }
  return table;
}

export function translate(text: string, locale: Locale): string | null {
  if (locale === "fr") return null;
  const key = normalize(text);
  const values = getTable().get(key);
  if (values) return values[INDEX[locale]];
  for (const [pattern, build] of PATTERNS) {
    const m = key.match(pattern);
    if (!m) continue;
    // Les fragments dynamiques (titres, noms) sont eux-mêmes traduits quand c'est possible
    const sub = (s: string) => translate(s, locale) ?? s;
    return build(m, sub)[INDEX[locale]];
  }
  return null;
}

export function isLocale(value: unknown): value is Locale {
  return typeof value === "string" && (LOCALES as readonly string[]).includes(value);
}

export function detectLocale(): Locale {
  try {
    const stored = localStorage.getItem(LANG_STORAGE_KEY);
    if (isLocale(stored)) return stored;
  } catch {
    // no-op
  }
  const langs = typeof navigator !== "undefined" ? navigator.languages ?? [navigator.language] : [];
  for (const lang of langs) {
    const code = lang?.slice(0, 2).toLowerCase();
    if (isLocale(code)) return code;
  }
  return "en";
}

/** Script inline exécuté avant le rendu : masque la page le temps d'appliquer la traduction. */
export const I18N_BOOT_SCRIPT = `(function(){try{var l=localStorage.getItem("${LANG_STORAGE_KEY}");if(!l){var a=navigator.languages||[navigator.language];l="en";for(var i=0;i<a.length;i++){var c=(a[i]||"").slice(0,2).toLowerCase();if(["fr","en","de","it","es"].indexOf(c)>-1){l=c;break}}}if(l!=="fr"){var d=document.documentElement;d.lang=l;d.classList.add("cp-i18n-pending");setTimeout(function(){d.classList.remove("cp-i18n-pending")},1500)}}catch(e){}})();`;
